import React, { useEffect, useState } from 'react';
import { styled } from '@mui/material/styles';
import ArrowForwardIosIcon from '@mui/icons-material/ArrowForwardIos';

import { CenterBox } from '@im/ui-lib/CenterBox';

import CircularProgress from '@mui/material/CircularProgress';
import Divider from '@mui/material/Divider';
import Link from '@mui/material/Link';
import ListItemIcon from '@mui/material/ListItemIcon';
import ListItemText from '@mui/material/ListItemText';
import MenuItem from '@mui/material/MenuItem';
import MenuList from '@mui/material/MenuList';
import Stack from '@mui/material/Stack';

import { useTranslate } from '@im/react-localization';
import { NavItem } from '../../nav';

type Props = {
  'data-testid'?: string;
  isAuthenticated?: boolean;
  isPrimary?: boolean;
  level: number;
  navItem: NavItem;
  onNavItemClick: (item: NavItem, level: number) => void;
  onNavItemHover: (item: NavItem, level: number) => void;
};

export function SideNavMenu(props: Readonly<Props>) {
  const {
    'data-testid': testid = 'required',
    isPrimary = false,
    level,
    navItem,
    onNavItemClick,
    onNavItemHover,
  } = props;
  const translate = useTranslate();
  const { navItems } = navItem;

  const [loading, setLoading] = useState(false);
  const [itemArr, setItemArr] = useState<NavItem[]>([]);

  const itemsToMap = Array.isArray(navItems) ? navItems : itemArr;

  const PRODUCTS = 'products';
  const VENDORS = 'vendors';
  const viewAllMap = [PRODUCTS, VENDORS];

  const getViewAllLinkByKey = (key: string) => {
    if (key === PRODUCTS) {
      return 'product/productsearch';
    } else if (key === VENDORS) {
      return '/cms/vendorsList';
    }
  };

  useEffect(() => {
    let active = true;
    if (Array.isArray(navItems)) {
      return;
    } else if (typeof navItems === 'function') {
      (async () => {
        setLoading(true);
        const arr = await navItems();
        if (active) {
          setItemArr(arr);
          setLoading(false);
        }
      })();
    }

    return () => {
      active = false;
    };
  }, [navItems]);

  return (
    <RootStack>
      {loading && (
        <CenterBox>
          <CircularProgress />
        </CenterBox>
      )}

      <MenuList
        sx={{
          maxHeight: `calc(100% - 135px)`,
          ...(navItem.key === VENDORS && {
            padding: '16px 8px 33px 8px !important',
          }),
        }}
      >
        {navItem.key === VENDORS && (
          <StyledMenuItemTitle
            dense
            key="Featured Vendors"
            style={{ cursor: 'auto' }}
          >
            <ListItemText>{translate('Dashboard.MyTopVendors')}</ListItemText>
          </StyledMenuItemTitle>
        )}

        <ScrollArea
          style={{
            maxHeight: '100%',
            overflowY: 'scroll',
          }}
        >
          {itemsToMap.map((item) => {
            const hasItems = Boolean(item.navItems);
            const cursor =
              item.route || item.url || item.onClick ? 'pointer' : 'auto';

            return (
              <StyledMenuItem
                data-testid={`${testid}-${item.key}-CategoryName`}
                dense
                disableGutters
                key={item.key}
                onMouseEnter={(e) => {
                  onNavItemHover(item, level);
                }}
                style={{ cursor: `${cursor}` }}
              >
                <ListItemText
                  onClick={
                    item.route || item.url || item.onClick
                      ? () => onNavItemClick(item, level)
                      : undefined
                  }
                >
                  {item.label || item.key}
                </ListItemText>

                {hasItems && (
                  <ListItemIcon
                    onClick={(e) => {
                      onNavItemHover(item, level);
                    }}
                  >
                    <ArrowForwardIosIcon
                      sx={{
                        height: '12px',
                        minWidth: 'inherit',
                        width: '12px',
                      }}
                    />
                  </ListItemIcon>
                )}
              </StyledMenuItem>
            );
          })}
        </ScrollArea>
      </MenuList>
      {viewAllMap.includes(navItem.key) && (
        <StyledStack
          isMaxReached={itemsToMap.length > 19}
          isPrimary={isPrimary}
        >
          <Divider />
          <Link
            onClick={() =>
              onNavItemClick(
                { key: 'ViewAll', route: getViewAllLinkByKey(navItem.key) },
                level
              )
            }
            variant="link"
          >
            {translate('ProductSearchResources.ViewAll')}
          </Link>
        </StyledStack>
      )}
    </RootStack>
  );
}

interface AvailabilityProps {
  isMaxReached?: boolean;
  isPrimary?: boolean;
}

const RootStack = styled(Stack)(({ theme }) => ({
  borderRadius: 0,
  borderRight: `1px solid ${theme.palette.divider}`,
  height: '100%',
  width: '240px',
}));

const ScrollArea = styled('div')(({ theme }) => ({
  '&::-webkit-scrollbar': {
    width: '6px',
  },
  '&::-webkit-scrollbar-thumb': {
    background: theme.palette.grey[300],
    borderRadius: '1px',
  },
  '&::-webkit-scrollbar-track': {
    borderRadius: '1px',
    boxShadow: 'none',
  },
  flexGrow: 1,
  overflowX: 'hidden',
  overflowY: 'auto',
}));

const StyledMenuItem = styled(MenuItem)(({ theme }) => ({
  '& .MuiListItemIcon-root': {
    height: 12,
    minWidth: 'inherit',
    width: 12,
  },
  '&:hover': {
    backgroundColor: theme.palette.grey[50],
    color: theme.palette.primary.dark,
  },
  '.MuiListItemText-primary': {
    cursor: 'pointer',
    fontSize: '14px',
    fontWeight: 400,
    lineHeight: '22px',
    whiteSpace: 'break-spaces',
  },
  marginLeft: 16,
  padding: 8,
  width: '92%',
}));

const StyledStack = styled(Stack, {
  shouldForwardProp: (props) => props !== 'isPrimary',
})<AvailabilityProps>(({ isMaxReached, isPrimary, theme }) => ({
  '& .MuiDivider-root': {
    borderColor: theme.palette.grey[300],
  },
  '& .MuiTypography-root': {
    color: '#0164B0',
    fontWeight: 400,
    marginLeft: '8px',
    padding: '8px 4px 4px 4px',
  },
  backgroundColor: 'transparent',
  bottom: isMaxReached ? '68px' : 0,
  padding: isPrimary ? '0 8px' : '0 8px 0 20px',
  position: isMaxReached ? 'absolute' : 'relative',
  width: '100%',
}));

const StyledMenuItemTitle = styled(MenuItem)(({ theme }) => ({
  '&:hover': {
    backgroundColor: theme.palette.common.white,
    color: theme.palette.common.black,
  },
  '.MuiListItemText-primary': {
    fontSize: '14px',
    fontWeight: 600,
    lineHeight: '22px',
  },
  marginLeft: 16,
  padding: 4,
  width: 188,
}));
